import { Outlet, useParams } from "react-router-dom";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs.ui";
import { useEffect, useState } from "react";
import { useGetPostsHttp } from "../../hooks/http/posts/get-posts.hook";
import { handleHttpError } from "../../../utils/utils";
import {
  PostStatus,
  type IPost,
} from "../../../types/models/post.models.types";
import { useToast } from "../../../hooks/use-toast";
import Spinner from "../../../components/spinner.components";
import { CampaignPost } from "../../components/core/campaign-post.core";
import usePostStore from "../../store/post.store";
import type { IPagination } from "../../../types/models/pagination.models.types";
import PaginationComponent from "../../components/core/pagination.core";

export default function DraftPostsPartial() {
  const [activeTab, setActiveTab] = useState<PostStatus>(PostStatus.draft);
  const { campaignId } = useParams();
  const [paginationData, setPaginationData] = useState<IPagination>();
  const [pageNo, setPageNo] = useState<number>(1);

  const { toast } = useToast();
  const { setData: setCampaignPosts, data: campaignPosts } = usePostStore();
  const {
    execute: getPostsHttp,
    fetching: gettingPosts,
    cancel: cancelGettingPosts,
  } = useGetPostsHttp();

  async function getPosts(status: PostStatus, page: number) {
    cancelGettingPosts();
    if (!campaignId) return;
    const response = await getPostsHttp({
      body: null,
      queryParams: {
        campaignId: campaignId,
        status,
        page,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setCampaignPosts(response.data.posts);
    setPaginationData(response.data.pagination);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getPosts(PostStatus.draft, 1);
  }, []);

  return (
    <div className="flex flex-col flex-1 h-full">
      <Tabs
        className="flex flex-col flex-1 h-full"
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value as PostStatus);
          setPageNo(1);
          getPosts(value as PostStatus, 1);
        }}
      >
        <div className="-top-4 z-10 md:sticky bg-white py-1.5 pl-10 border-black border-t border-b w-full">
          <TabsList className="flex justify-between w-96 font-cabinetgrotesk">
            <TabsTrigger value={PostStatus.draft}>Pending Approval</TabsTrigger>
            <TabsTrigger value={PostStatus.draftApproved}>
              Draft Approved
            </TabsTrigger>
            <TabsTrigger value={PostStatus.draftRejected}>
              Draft Rejected
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent className="flex flex-col flex-1 h-full" value={activeTab}>
          {gettingPosts ? (
            <div className="flex justify-center items-center w-full h-full">
              <Spinner width={20} />
            </div>
          ) : (
            <>
              <div className="flex-1 p-5">
                <div className="gap-6 grid grid-cols-3">
                  {campaignPosts?.map((post: IPost) => (
                    <CampaignPost key={post.id} post={post} />
                  ))}
                </div>
              </div>

              {/* Pagination */}
              {paginationData && (
                <div className="flex justify-center items-center py-4">
                  <PaginationComponent
                    paginationData={paginationData}
                    fetching={gettingPosts}
                    onClick={() => {
                      const nextPage = pageNo + 1;
                      setPageNo(nextPage);
                      getPosts(activeTab, nextPage);
                    }}
                  />
                </div>
              )}
            </>
          )}
          {!gettingPosts && campaignPosts?.length === 0 && (
            <div className="flex justify-center items-center w-full h-full">
              <p className="text-gray-500">No posts found</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
      <Outlet />
    </div>
  );
}
